import React, { useEffect, useState } from 'react'
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridRowsProp,
  type GridSlots,
} from '@mui/x-data-grid'
import { useLang } from '~/hooks/useLang'
import {
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Typography,
} from '@mui/material'
import Map from '~/features/Map/components/Map'
import { LatLng } from 'leaflet'
import { Crew, DeviceLogs } from '../../interfaces'
import { ptBR } from '@mui/x-data-grid/locales'
import NoRowsOverlay from '~/components/NoRowsOverlay'
import VisibilityIcon from '@mui/icons-material/Visibility'
import dayjs from 'dayjs'
import { useHookstate } from '@hookstate/core'
import {
  sortedCrew,
  type DeviceLog,
} from '~/features/Device/components/DeviceInfoDialog'
import { maskPhoneNumber } from '~/features/Dispatch/utils/stringsManipulations'
import Zoom from 'react-medium-image-zoom'
import { mountUrlRequest } from '~/services/fetch'
import useSystemState from '~/features/System/stores/SystemState'
import CloseIcon from '@mui/icons-material/Close'
import { useMarkerState } from '~/features/Map/stores/MapState'
interface ListDeviceLogsProps {
  readonly devices: readonly DeviceLogs[]
  readonly children?: React.ReactNode
}

// eslint-disable-next-line max-lines-per-function
export default function ListDeviceLogs({
  devices,
  children,
}: ListDeviceLogsProps) {
  const { translate } = useLang()
  const [rows, setRows] = useState<GridRowsProp>(devices)
  const { forceViewMap } = useMarkerState()
  const [rowSelectedModel, setRowSelectedModel] = useState<DeviceLogs>()
  const deviceLogState = useHookstate<DeviceLog | null>(null)
  const { systemConfig } = useSystemState()
  const [open, setOpen] = useState(false)

  const handleOpen = () => setOpen(true)

  const columns: readonly GridColDef[] = [
    { field: 'id' },
    {
      field: 'commander',
      headerName: translate('Commander'),
      flex: 1,
      filterable: false,
      sortable: false,
    },
    {
      field: 'connectionOrigin',
      headerName: translate('Origin'),
      flex: 1,
      filterable: false,
      sortable: false,
    },
    {
      field: 'dateConnected',
      headerName: translate('Connected'),
      flex: 1,
      type: 'dateTime',
      valueFormatter: (value) =>
        value ? dayjs(value).format('DD/MM/YYYY HH:mm') : translate('Uninformed'),
    },
    {
      field: 'dateDisconnected',
      headerName: translate('Disconnected'),
      flex: 1,
      type: 'dateTime',
      valueFormatter: (value) =>
        value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '',
    },
    {
      field: 'phone',
      headerName: translate('Phone'),
      flex: 1,
      filterable: false,
      sortable: false,
    },
    {
      field: 'task',
      headerName: translate('Task'),
      flex: 1,
      filterable: false,
      sortable: false,
    },
    {
      field: 'dispatchGroup',
      headerName: translate('Dispatch Group'),
      flex: 1,
      filterable: false,
      sortable: false,
    },
    {
      field: 'deviceType',
      headerName: translate('Device Type'),
      flex: 1,
      filterable: false,
      sortable: false,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      flex: 0.2,
      getActions: ({ id, row }) => [
        <GridActionsCellItem
          key={id}
          icon={<VisibilityIcon />}
          label="export"
          onClick={(event) => handleOnRowSelection(event, row)}
        />,
      ],
    },
  ]

  const getPosition = {
    Comandante: deviceLogState.get()?.device?.device_type?.label_commander
      ? deviceLogState.get()?.device?.device_type.label_commander
      : 'Comandante',
    Motorista: deviceLogState.get()?.device?.device_type?.label_driver
      ? deviceLogState.get()?.device?.device_type.label_driver
      : 'Motorista',
    Patrulheiro: deviceLogState.get()?.device?.device_type?.label_patrolmans
      ? deviceLogState.get()?.device?.device_type.label_patrolmans
      : 'Patrulheiro',
  }

  const getCrewCommander = (crew: readonly Crew[]) => {
    return crew.find((commander) => commander.position.id === 1)?.user
  }

  const getDeviceCondition = (conditionId) => {
    switch (conditionId) {
      case 0:
        return 'Em conformidade'
      case 1:
        return 'Necessário manutenção simples'
      case 2:
        return 'Veículo fora de condições de uso'
    }
  }

  useEffect(() => {
    const devicesRows = devices.map((deviceRaw: DeviceLogs, index) => {
      const {
        id,
        user,
        date_connected: dateConnected,
        date_disconnected: dateDisconnected,
        latitude_connected: latitudeConnected,
        longitude_connected: longitudeConnected,
        latitude_disconnected: latitudeDisconnected,
        longitude_disconnected: longitudeDisconnected,
        km_initial: kmInitial,
        dispatch_group: dispatchGroup,
        task,
        phone,
        device,
        crew,
        station,
      } = deviceRaw
      return {
        id: id,
        commander: getCrewCommander(crew)
          ? getCrewCommander(crew)?.registration
            ? `[${getCrewCommander(crew)?.registration}] ${
                getCrewCommander(crew)?.representation_name
              }`
            : getCrewCommander(crew)?.representation_name
          : translate('Uninformed'),
        connectionOrigin: station?.name
          ? `Estação - ${station?.name}`
          : device?.name
          ? `Dispositivo - ${device?.name}`
          : '',
        dateConnected: dateConnected ? new Date(dateConnected) : null,
        dateDisconnected: dateDisconnected ? new Date(dateDisconnected) : null,
        phone: phone ? phone : translate('Uninformed'),
        task: task ? task : translate('Uninformed'),
        dispatchGroup: dispatchGroup?.name
          ? dispatchGroup?.name
          : translate('Uninformed'),
        deviceType: device?.device_type?.name
          ? device?.device_type?.name
          : translate('Uninformed'),
        locationConnected:
          latitudeConnected && longitudeConnected
            ? [latitudeConnected, longitudeConnected]
            : translate('Uninformed'),
        locationDisconnected:
          latitudeDisconnected && longitudeDisconnected
            ? [latitudeDisconnected, longitudeDisconnected]
            : translate('Uninformed'),
        kmInitial: kmInitial ? kmInitial : translate('Uninformed'),
      }
    })
    setRows(devicesRows)
  }, [devices])

  const handleOnRowSelection = (event, row) => {
    if (!devices.length) {
      return
    }
    handleOpen()

    const dataDevice = devices.find((device) => device.id === row.id)
    if (dataDevice) {
      setRowSelectedModel(dataDevice)
      forceViewMap().position.set(
        new LatLng(
          rowSelectedModel?.latitude_connected
            ? rowSelectedModel?.latitude_connected
            : 0,
          rowSelectedModel?.longitude_connected
            ? rowSelectedModel?.longitude_connected
            : 0
        )
      )
    }
  }
  const clickOutModalClose = (event, reason) => {
    if (reason !== 'backdropClick') {
      setOpen(false)
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={clickOutModalClose}
        fullWidth
        maxWidth={'sm'}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        disableEscapeKeyDown={true}
      >
        <DialogTitle
          display={'flex'}
          alignItems={'center'}
          justifyContent={'space-between'}
        >
          {translate('Conexão do dispositivo')}
          <IconButton aria-label="close" onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Card style={{ height: '240px' }}>
            <Map
              position={
                new LatLng(
                  rowSelectedModel?.latitude_connected
                    ? rowSelectedModel?.latitude_connected
                    : 0,
                  rowSelectedModel?.longitude_connected
                    ? rowSelectedModel?.longitude_connected
                    : 0
                )
              }
              enableLayerControl={false}
            >
              {children}
            </Map>
          </Card>

          <CardContent sx={{ overflowY: 'scroll', marginTop: 2 }}>
            <Typography>
              {translate('date_connected')}
              {': '}
              <b>
                {rowSelectedModel?.date_connected
                  ? dayjs(rowSelectedModel?.date_connected).isValid()
                    ? dayjs(rowSelectedModel?.date_connected).format(
                        'DD/MM/YYYY HH:mm'
                      )
                    : ''
                  : ''}
              </b>
            </Typography>
            {rowSelectedModel?.date_disconnected && (
              <Typography>
                {translate('date_disconnected')}
                {': '}
                <b>
                  {rowSelectedModel?.date_disconnected
                    ? dayjs(rowSelectedModel?.date_disconnected).isValid()
                      ? dayjs(rowSelectedModel?.date_disconnected).format(
                          'DD/MM/YYYY HH:mm'
                        )
                      : ''
                    : ''}
                </b>
              </Typography>
            )}

            {!rowSelectedModel?.date_disconnected && (
              <Typography>
                {translate('Status')}
                {': '}
                <b>
                  {`${
                    rowSelectedModel?.device.status.name
                      ? rowSelectedModel?.device.status.name
                      : translate('Uninformed')
                  }`}
                </b>
              </Typography>
            )}
            <Typography>
              {translate('Dispatch Group')}
              {': '}
              <b>
                {`${
                  rowSelectedModel?.dispatch_group?.name
                    ? rowSelectedModel?.dispatch_group?.name
                    : translate('Uninformed')
                }`}
              </b>
            </Typography>
            <Typography>
              {translate('Task')}
              {': '}
              <b>
                {`${
                  rowSelectedModel?.task
                    ? rowSelectedModel?.task
                    : translate('Uninformed')
                }`}
              </b>
            </Typography>

            <Typography>
              {rowSelectedModel?.device.device_type.label_km_initial
                ? rowSelectedModel?.device.device_type.label_km_initial
                : translate('Initial Kilometer')}
              {': '}
              <b>
                {`${
                  rowSelectedModel?.km_initial
                    ? rowSelectedModel?.km_initial
                    : translate('Uninformed')
                }`}
              </b>
            </Typography>

            <Typography>
              {translate('License plate')}
              {': '}
              <b>
                {`${
                  rowSelectedModel?.vehicle
                    ? rowSelectedModel?.vehicle.plate
                    : translate('Uninformed')
                }`}
              </b>
            </Typography>

            <Typography>
              {translate('Tracking')}
              {': '}
              <b>
                {`${
                  rowSelectedModel?.tracking_id
                    ? rowSelectedModel?.tracking_id
                    : translate('Uninformed')
                }`}
              </b>
            </Typography>

            {rowSelectedModel?.city && (
              <Typography>
                {translate('City')}
                {': '}
                <b>
{`${rowSelectedModel?.city?.name}`}
</b>
              </Typography>
            )}

            {rowSelectedModel?.operation && (
              <Typography>
                {translate('Operation')}
                {': '}
                <b>
{`${rowSelectedModel?.operation?.name}`}
</b>
              </Typography>
            )}
            {Boolean(Number(systemConfig().featureToPocRs.get())) &&
              rowSelectedModel?.device_condition !== null && (
                <Typography>
                  {translate('Situação da viatura')}
                  {': '}
                  <b>
                    {getDeviceCondition(rowSelectedModel?.device_condition)}
                  </b>
                </Typography>
              )}

            <Typography>
              {translate('Notes')}
              {': '}
              <b>
                {`${
                  rowSelectedModel?.notes
                    ? rowSelectedModel?.notes
                    : translate('Uninformed')
                }`}
              </b>
            </Typography>

            {!sortedCrew?.length ? (
              <Grid item xs={12} key={0}>
                {translate('Nothing to this vehicle')}
              </Grid>
            ) : (
              sortedCrew(rowSelectedModel?.crew)
                ?.map((crew, index) => {
                  if ('position' in crew) {
                    return (
                      <Grid
                        item
                        key={index}
                        xs={12}
                        marginTop={1}
                        marginBottom={1}
                      >
                        <Typography sx={{ fontWeight: 'bold' }}>
                          {`${getPosition[crew.position.name]}`}
{' '}
                        </Typography>
                        <Typography>
                          {crew.user?.representation_name
                            ? `${
                                crew.user?.registration
                                  ? `[${crew.user?.registration}] `
                                  : ''
                              }${crew.user?.representation_name}`
                            : ''}
                        </Typography>
                        <Typography>
                          {crew.position.id === 1 && `${translate('Contact')}:`}
{' '}
                          <b>
                            {crew.position.id === 1 && rowSelectedModel?.phone
                              ? `${maskPhoneNumber(rowSelectedModel?.phone)}`
                              : ''}
                          </b>
                        </Typography>
                      </Grid>
                    )
                  }
                  return false
                })
                .filter((elem) => elem !== false)
            )}

            <Grid>
              <Typography>
                {`Dispositivo conectado por ${
                  rowSelectedModel?.user.registration
                    ? `[${rowSelectedModel?.user.registration}]`
                    : ''
                } ${rowSelectedModel?.user.representation_name} ${
                  rowSelectedModel?.station
                    ? `pela estação ${rowSelectedModel?.station.name}`
                    : `pelo dispositivo ${rowSelectedModel?.device.name}`
                }`}
              </Typography>
            </Grid>
            <Grid>
              {rowSelectedModel?.media_path &&
                Number(systemConfig().featureToPocRs.get()) === 1 && (
                  <Grid
                    width={'100%'}
                    display={'flex'}
                    alignItems={'center'}
                    justifyContent={'center'}
                    marginTop={'24px'}
                  >
                    <Zoom zoomMargin={50}>
                      <img
                        key={rowSelectedModel?.id}
                        srcSet={mountUrlRequest(
                          `/device/logs/get-media/${rowSelectedModel.media_path}`
                        )}
                        src={mountUrlRequest(
                          `/device/logs/get-media/${rowSelectedModel.media_path}`
                        )}
                        loading="lazy"
                        width="500"
                        style={{
                          height: '280px',
                          width: '100%',
                        }}
                      />
                    </Zoom>
                  </Grid>
                )}
            </Grid>
          </CardContent>
        </DialogContent>
      </Dialog>
      <DataGrid
        localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
        autoHeight
        onRowSelectionModelChange={handleOnRowSelection}
        rows={rows}
        columns={columns}
        disableColumnMenu
        slots={{
          loadingOverlay: LinearProgress as GridSlots['loadingOverlay'],
        }}
        paginationMode={'client'}
        pageSizeOptions={[]}
        keepNonExistentRowsSelected
        disableColumnSelector
        disableRowSelectionOnClick
        columnVisibilityModel={{
          id: false,
        }}
      />
    </>
  )
}
